import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Box,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// Helper function to convert unicode emoji strings to actual emojis
const parseEmoji = (text) => {
  if (!text) return text;
  // Convert unicode emoji sequences like \uD83D\uDE80 to actual emojis
  return text.replace(/\\u[\dA-F]{4}\\u[\dA-F]{4}|\\u[\dA-F]{4}/gi, (match) => {
    try {
      return JSON.parse(`"${match}"`);
    } catch (e) {
      return match;
    }
  });
};

const TasksTable = ({
  tasks,
  sortBy,
  sortOrder,
  onSort,
  getUserName,
  getUserEmail,
  onMessageUser,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const handleMenuOpen = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleCopyTaskId = () => {
    if (selectedTask) {
      navigator.clipboard.writeText(selectedTask.taskId);
    }
    handleMenuClose();
  };

  const handleCopyUserId = () => {
    if (selectedTask) {
      navigator.clipboard.writeText(selectedTask.userId);
    }
    handleMenuClose();
  };

  const handleMessageUser = () => {
    if (selectedTask) {
      onMessageUser(selectedTask.userId);
    }
    handleMenuClose();
  };

  const getStatusChip = (task) => {
    if (task.completed) {
      return (
        <Chip
          label="Completed"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    }
    if (task.frequency) {
      return (
        <Chip
          label="Recurring"
          color="primary"
          size="small"
          variant="outlined"
        />
      );
    }
    if (task.deadline && task.deadline < dayjs().unix()) {
      return (
        <Chip label="Overdue" color="error" size="small" variant="outlined" />
      );
    }
    if (task.targeted) {
      return (
        <Chip
          label="Targeted"
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip label="Active" color="default" size="small" variant="outlined" />
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => onSort("name")}
              >
                Task Name
              </TableSortLabel>
            </TableCell>
            <TableCell>User</TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "created"}
                direction={sortBy === "created" ? sortOrder : "asc"}
                onClick={() => onSort("created")}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "completed"}
                direction={sortBy === "completed" ? sortOrder : "asc"}
                onClick={() => onSort("completed")}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell>Labels</TableCell>
            <TableCell>Attributes</TableCell>
            <TableCell align="right">Options</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tasks.map((task) => {
            const userName = getUserName(task.userId);
            const userEmail = getUserEmail(task.userId);

            return (
              <TableRow key={task.taskId} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {parseEmoji(task.name) || "Untitled Task"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        wordBreak: "break-all",
                      }}
                    >
                      {task.taskId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {userName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {userEmail}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.65rem",
                        display: "block",
                        wordBreak: "break-all",
                      }}
                    >
                      {task.userId}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {task.created
                        ? dayjs(task.created).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.created ? dayjs(task.created).fromNow() : ""}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{getStatusChip(task)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {task.labels?.slice(0, 3).map((label, idx) => (
                      <Chip
                        key={idx}
                        label={label}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {task.labels?.length > 3 && (
                      <Chip label={`+${task.labels.length - 3}`} size="small" />
                    )}
                    {(!task.labels || task.labels.length === 0) && (
                      <Typography variant="caption" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      minWidth: 100,
                    }}
                  >
                    {task.attributes?.difficulty && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ minWidth: 20, fontSize: "0.65rem" }}
                        >
                          Diff
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: 4,
                            backgroundColor: "#f0f0f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(task.attributes.difficulty / 10) * 100}%`,
                              height: "100%",
                              backgroundColor: "#ff5722",
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", minWidth: 12 }}
                        >
                          {task.attributes.difficulty}
                        </Typography>
                      </Box>
                    )}
                    {task.attributes?.importance && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ minWidth: 20, fontSize: "0.65rem" }}
                        >
                          Imp
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: 4,
                            backgroundColor: "#f0f0f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(task.attributes.importance / 10) * 100}%`,
                              height: "100%",
                              backgroundColor: "#ff9800",
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", minWidth: 12 }}
                        >
                          {task.attributes.importance}
                        </Typography>
                      </Box>
                    )}
                    {task.attributes?.fun && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ minWidth: 20, fontSize: "0.65rem" }}
                        >
                          Fun
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            height: 4,
                            backgroundColor: "#f0f0f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${(task.attributes.fun / 10) * 100}%`,
                              height: "100%",
                              backgroundColor: "#4caf50",
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", minWidth: 12 }}
                        >
                          {task.attributes.fun}
                        </Typography>
                      </Box>
                    )}
                    {!task.attributes?.difficulty &&
                      !task.attributes?.importance &&
                      !task.attributes?.fun && (
                        <Typography variant="caption" color="text.secondary">
                          None
                        </Typography>
                      )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, task)}
                  >
                    <MoreVert sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {tasks.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No tasks found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCopyTaskId}>Copy Task ID</MenuItem>
        <MenuItem onClick={handleCopyUserId}>Copy User ID</MenuItem>
        <MenuItem onClick={handleMessageUser}>Message User</MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default TasksTable;
